import * as React from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { Link } from 'react-router-dom';
import { Spinner } from '@patternfly/react-core';
import { sortable, cellWidth } from '@patternfly/react-table';
import { PageSection, Title, Modal, ModalVariant } from '@patternfly/react-core';
import { BullhornIcon, EllipsisVIcon } from '@patternfly/react-icons';
import { PatternflyTable } from '../../components/PatternflyTable';
import { ErrorView } from '../error/error';
import { useWanContext } from '../../contexts/wan.context';
import { useSearch } from '../../lib/use-search';
import { wanSelectors } from '../../common/configuration/configuration.selectors';
import { actionCreators } from '../../common/configuration/configuration.reducer';
import { Wan } from '../../types';
import ThemeContext from '@app/providers/contexts/ThemeContext';
import Page from '@app/components/Page';
import { Card, CardBody, Col, Button, Row, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { Column, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, Updater, useReactTable } from '@tanstack/react-table';
import { MdArrowDownward, MdArrowUpward, MdCircle, MdUnfoldMore } from 'react-icons/md';
import Loader from '@app/components/Loader';
import Wans from '@app/api/wan/wan.actions';
import Swal from 'sweetalert2';
import UserSession from '@app/common/user-session';

type ColumnFiltersState = ColumnFilter[];
interface ColumnFilter {
  id: string;
  value: unknown;
}

interface ThemeContextType {
  theme: string;
  setTheme: (theme: string) => void;
}

const WanActionsMenu = ({ wan, basePath, isManager, onDelete }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Dropdown isOpen={isOpen} toggle={() => setIsOpen(open => !open)} direction="start">
      <DropdownToggle
        color="link"
        className="list-actions-toggle"
        aria-label={`Actions for ${wan.name}`}
      >
        <EllipsisVIcon />
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem tag={Link} to={`${basePath}/wan/edit/${wan.id}`}>Edit</DropdownItem>
        <DropdownItem divider />
        <DropdownItem
          className="list-action-danger"
          disabled={!isManager}
          onClick={() => onDelete(wan.id, wan.name)}
        >
          Delete
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export const WanList: React.FC = () => {
  const dispatch = useDispatch();
  const { isFailed, isLoading, isSuccess } = useWanContext();
  const { theme } = React.useContext(ThemeContext) as ThemeContextType;



  const wans = useSearch(useSelector(wanSelectors.selectAll));
  const [expandedRows, setExpandedRows] = React.useState({});

  const basePath = process.env.REACT_APP_BASE_PATH || '';
  const isManager = UserSession.getParam('role')?.toLowerCase() === 'manager';

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  )
  
  const [sorting, setSorting] = React.useState([
    {
      id: 'name',
      desc: false,
    },
  ]);

  const toggleRow = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const statuses = [
    "#85ba3b",
    "#e93a54",
    "#000"
  ];

  const generateRandomNumber = () => {
    const randomNumber = Math.floor(Math.random() * statuses.length);
    return statuses[randomNumber];
  }

  const getColumns = () => {
    return [
      { title: 'Name', transforms: [sortable] },
      { title: 'ID' },
      { title: 'Primary Controller' },
      { title: 'Primary Gateway', transforms: [sortable] },
      { title: 'Sites' },
      { title: '', transforms: [cellWidth(10)] }
    ];
  };

  const wanFromItem = React.useCallback(
    (wan: Wan) => {
      const wanSites = (wan as any).Sites || [];
      const wanController = (wan as any).activeController;
      const wanGateway = (wan as any).activeGateway;
      return {
        cells: [
          { title: <Link to={{ pathname: `${basePath}/wan/${wan.id}` }}>{wan.name}</Link> },
          { title: wanSites.length },
          { title: wanController?.name },
          { title: wanGateway?.ip4Address },
          { title: wan.id },
        ],
      };
    },
    []
  );

  const generateWans = React.useCallback(
    (wan: Wan) => {
      const wanSites = (wan as any).Sites || [];
      const wanController = (wan as any).activeController;
      const activeGateway = (wan as any).activeGateway;
      const standbyGateway = (wan as any).standbyGateway;
      
      return {
        name: wan.name,
        sitesCount: wanSites.length,
        controller: wanController?.name,
        ip: activeGateway?.ip4Address,
        id: wan.id,
        sites: wanSites,
        gateways: {
          active: activeGateway,
          standby: standbyGateway
        }
      };
    },
    []
  );

  const wansData = React.useMemo(() => wans.map(generateWans), [wans, generateWans]);

  const columns = React.useMemo(() => {
    const unorderedColumns = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <>
          <strong>
            <Link to={`${basePath}/wan/${row.original.id}`} className="wan-name">
              {row.original.name}
            </Link>
          </strong>
        </>
      ),
      sortingFn: (rowA, rowB, columnId) => {
        const aVal = rowA.original.name || '';
        const bVal = rowB.original.name || '';
        const aEmpty = !aVal;
        const bEmpty = !bVal;
        
        const currentSort = sorting.find(s => s.id === columnId);
        const isDescending = currentSort?.desc || false;
        
        if (aEmpty && bEmpty) return 0;
        
        if (isDescending) {
          if (aEmpty) return -1;
          if (bEmpty) return 1;
        } else {
          if (aEmpty) return 1;
          if (bEmpty) return -1;
        }
        
        return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      },
    },
    {
      accessorKey: 'sitesCount',
      header: 'Sites',
      enableColumnFilter: false,
      sortingFn: (rowA, rowB, columnId) => {
        const aVal = rowA.original.sitesCount ?? 0;
        const bVal = rowB.original.sitesCount ?? 0;
        return aVal - bVal;
      },
    },
    {
      accessorKey: 'controller',
      header: 'Primary Controller',
      sortingFn: (rowA, rowB, columnId) => {
        const aVal = rowA.original.controller || '';
        const bVal = rowB.original.controller || '';
        const aEmpty = !aVal;
        const bEmpty = !bVal;
        
        const currentSort = sorting.find(s => s.id === columnId);
        const isDescending = currentSort?.desc || false;
        
        if (aEmpty && bEmpty) return 0;
        
        if (isDescending) {
          if (aEmpty) return -1;
          if (bEmpty) return 1;
        } else {
          if (aEmpty) return 1;
          if (bEmpty) return -1;
        }
        
        return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      },
    },
    {
      accessorKey: 'ip',
      header: 'Primary Gateway',
      sortingFn: (rowA, rowB, columnId) => {
        const aVal = rowA.original.ip || '';
        const bVal = rowB.original.ip || '';
        const aEmpty = !aVal;
        const bEmpty = !bVal;
        
        const currentSort = sorting.find(s => s.id === columnId);
        const isDescending = currentSort?.desc || false;
        
        if (aEmpty && bEmpty) return 0;
        
        if (isDescending) {
          if (aEmpty) return -1;
          if (bEmpty) return 1;
        } else {
          if (aEmpty) return 1;
          if (bEmpty) return -1;
        }
        
        return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      },
    },
    {
      accessorKey: 'id',
      header: 'ID',
      size: 290,
      cell: ({ row }) => (
        <div style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
          {row.original.id}
        </div>
      ),
      sortingFn: (rowA, rowB, columnId) => {
        const aVal = rowA.original.id || '';
        const bVal = rowB.original.id || '';
        const aEmpty = !aVal;
        const bEmpty = !bVal;
        
        const currentSort = sorting.find(s => s.id === columnId);
        const isDescending = currentSort?.desc || false;
        
        if (aEmpty && bEmpty) return 0;
        
        if (isDescending) {
          if (aEmpty) return -1;
          if (bEmpty) return 1;
        } else {
          if (aEmpty) return 1;
          if (bEmpty) return -1;
        }
        
        return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      },
    },
    {
      id: 'actions',
      header: '',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <WanActionsMenu
          wan={row.original}
          basePath={basePath}
          isManager={isManager}
          onDelete={deleteWanItem}
        />
      ),
      size: 50,
    },
    ];

    const columnOrder = ['name', 'id', 'controller', 'ip', 'sitesCount', 'actions'];
    return columnOrder.map((columnId) => unorderedColumns.find((column) => (column.id || column.accessorKey) === columnId)!);
  },[sorting, basePath, isManager]);

  const table = useReactTable({
    data: wansData,
    columns,
    filterFns: {},
    state: {
      columnFilters,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    initialState: {
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
    },
    manualPagination: false,
  });

  const editWan = React.useCallback(
    (_, __, data) => {
      const wanId = data['wan-id']['title'];
      dispatch(push(`/wan/edit/${wanId}`));
    },
    [dispatch]
  );

  const deleteWanItem = (wanId, wanName) => {
    Swal.fire({
      title: 'Confirm Action',
      html: `<div style="text-align: left;">
               <p>Are you sure you want to delete this pWAN?</p>
               <strong>Name:</strong> ${wanName}
             </div>`,
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(actionCreators.wanDeleteRequest(wanId));
      }
    });
  };

  const deleteRow = React.useCallback(() => {

  }, [dispatch]);

  if (isFailed) {
    return (
      <React.Fragment>
        <Helmet>
          <title>pWANs</title>
        </Helmet>
        <Page
          tag="div"
          className={`cr-page px-3 pt-2 wans-list ${theme}`}
          title=""
          breadcrumbs={[{ name: 'pWANs', active: true }]}
          actionButton={
            <Link to={`${basePath}/wan/new`}>
              <Button className="ethica-button-green" size="sm">Add pWAN</Button>
            </Link>
          }
        >
          <Row>
            <Col lg={12} md={12} sm={12} xs={12} className="mb-3">
              <div className="controllers-card-wrapper">
                <Card>
                    <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>ID</th>
                          <th>Primary Controller</th>
                          <th>Primary Gateway</th>
                          <th>Sites</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colSpan={6} className="text-center">No data available</td>
                        </tr>
                      </tbody>
                    </table>
                </Card>
              </div>
            </Col>
          </Row>
        </Page>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>pWANs</title>
      </Helmet>
      {isLoading ? (
        <Loader />
      ) : (
      <Page
            tag="div"
            className={`cr-page px-3 pt-2 wans-list ${theme}`}
            title=""
            breadcrumbs={[{ name: 'pWANs', active: true }]}
            actionButton={
              <Link to={`${basePath}/wan/new`}>
                <Button className="ethica-button-green" size="sm">Add pWAN</Button>
              </Link>
            }
          >
          <Row>
            <Col lg={12} md={12} sm={12} xs={12} className="mb-3">
              <div className="controllers-card-wrapper">
                <Card className="p-3">
                    {(wans?.length ?? 0) === 0 ? (
                      <>
                        <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>ID</th>
                              <th>Primary Controller</th>
                              <th>Primary Gateway</th>
                              <th>Sites</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td colSpan={6} className="text-center">No data available</td>
                            </tr>
                          </tbody>
                        </table>
                      </>
                    ) : (
                      <>
                        <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                          <thead>
                            {table.getHeaderGroups().map(headerGroup => (
                              <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                  <th
                                    key={header.id}
                                    colSpan={header.colSpan}
                                    style={{
                                      width: header.column.id === 'id' ? '290px' : header.column.id === 'actions' ? '50px' : undefined,
                                      minWidth: header.column.id === 'id' ? '290px' : undefined,
                                    }}
                                  >
                                    {header.isPlaceholder ? null : (
                                      <div className={`d-flex align-items-center ${header.column.id === 'actions' ? 'justify-content-end' : 'justify-content-start'}`}>
                                        <div
                                          {...{
                                            className: header.column.getCanSort()
                                              ? 'select-none sites-header'
                                              : 'sites-header',
                                            onClick: header.column.getToggleSortingHandler(),
                                          }}
                                        >
                                          {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                          )}

                                          {header.column.getCanSort() && (
                                            {
                                              asc: <MdArrowUpward className="ms-2" />,
                                              desc: <MdArrowDownward className="ms-2" />,
                                            }[header.column.getIsSorted() as string] ?? (
                                              <MdUnfoldMore className="ms-2 opacity-50" />
                                            )
                                          )}
                                        </div>
                                        {header.column.getCanFilter() ? (
                                          <div className="d-flex justify-content-end">
                                            <Filter column={header.column} />
                                          </div>
                                        ) : null}
                                      </div>
                                    )}
                                  </th>
                                ))}
                              </tr>
                            ))}
                          </thead>
                          <tbody>
                            {table.getRowModel().rows.map(row => (
                              <React.Fragment key={row.id}>
                                <tr>
                                  {row.getVisibleCells().map(cell => (
                                    <td
                                      key={cell.id}
                                      className={cell.column.id === 'actions' ? 'text-end' : 'text-start'}
                                      style={{
                                        overflow: cell.column.id === 'actions' ? 'visible' : 'hidden',
                                        width: cell.column.id === 'id' ? '290px' : cell.column.id === 'actions' ? '50px' : undefined,
                                        minWidth: cell.column.id === 'id' ? '290px' : undefined,
                                        whiteSpace: cell.column.id === 'id' ? 'nowrap' : undefined,
                                        position: cell.column.id === 'actions' ? 'relative' : undefined,
                                        zIndex: cell.column.id === 'actions' ? 2 : undefined,
                                      }}
                                    >
                                      {row.original.sites && row.original.sites.length > 0 && cell.column.columnDef.header === 'Name' ? (
                                        <>
                                          <button onClick={() => toggleRow(row.id)} className="table-arrow">
                                            {expandedRows[row.id] ? '▼' : '▶'}
                                          </button>
                                          <strong>{flexRender(cell.column.columnDef.cell, cell.getContext())}</strong>
                                        </>
                                      ) : cell.column.columnDef.header === 'Sites' ? (
                                        <span className="site-count">{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
                                      ) : (
                                        <span className={`no-arrow d-flex flex-wrap-nowrap w-100 ${cell.column.id === 'actions' ? 'justify-content-end' : ''}`}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
                                      )}
                                    </td>
                                  ))}
                                </tr>
                                {expandedRows[row.id] && row.original.sites && row.original.sites.length > 0 && (
                                  <tr>
                                    <td colSpan={6} style={{ padding: '0', borderTop: '1px solid #dee2e6' }}>
                                      <div style={{ margin: '0', padding: '10px 0', borderBottom: 'none', width: '100%' }}>
                                        <div style={{ 
                                          display: 'flex',
                                          width: '100%',
                                          paddingLeft: '34px',
                                          borderBottom: '1px solid #dee2e6',
                                          backgroundColor: '#f8f9fa'
                                        }}>
                                          <div style={{ 
                                            width: '30%',
                                            padding: '8px',
                                            fontSize: '12px',
                                            color: '#333',
                                            fontWeight: '600',
                                            textAlign: 'left'
                                          }}>
                                            Site Name
                                          </div>
                                          <div style={{ 
                                            width: '70%',
                                            padding: '8px',
                                            fontSize: '12px',
                                            color: '#333',
                                            fontWeight: '600',
                                            textAlign: 'left'
                                          }}>
                                            ID
                                          </div>
                                        </div>
                                        {row.original.sites.map((site, index) => (
                                          <div 
                                            key={index} 
                                            className="expanded-gateway-row"
                                            style={{ 
                                              display: 'flex',
                                              width: '100%',
                                              paddingLeft: '34px',
                                              borderTop: index === 0 ? 'none' : '1px solid #dee2e6'
                                            }}
                                          >
                                            <div style={{ 
                                              width: '30%',
                                              padding: '8px',
                                              fontSize: '12px',
                                              color: '#222222',
                                              textAlign: 'left'
                                            }}>
                                              {(site as any).name || site.address1}
                                            </div>
                                            <div style={{ 
                                              width: '70%',
                                              padding: '8px',
                                              fontSize: '12px',
                                              color: '#222222',
                                              textAlign: 'left'
                                            }}>
                                              {site.id}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>

                        {/* Пагинация */}
                        <div className="d-flex items-center justify-content-center align-items-center flex-wrap mt-2">
                          <div className="me-2">
                            <span className="flex items-center gap-1">
                              Go to page:
                              <input
                                type="number"
                                min="1"
                                max={table.getPageCount()}
                                defaultValue={table.getState().pagination.pageIndex + 1}
                                onChange={e => {
                                  const page = e.target.value ? Number(e.target.value) - 1 : 0
                                  table.setPageIndex(page)
                                }}
                                className="border p-1 rounded w-16 ms-2"
                              />
                            </span>
                          </div>
                          <div>
                              <button
                                className="border rounded p-1 me-1"
                                onClick={() => table.setPageIndex(0)}
                                disabled={!table.getCanPreviousPage()}
                              >
                                {'<<'}
                              </button>
                              <button
                                className="border rounded p-1 me-1"
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                              >
                                {'<'}
                              </button>
                              <span className="flex items-center me-1">
                                <strong>
                                  {table.getState().pagination.pageIndex + 1} of{' '}
                                  {table.getPageCount()}
                                </strong>
                              </span>
                              <button
                                className="border rounded p-1 me-1"
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                              >
                                {'>'}
                              </button>
                              <button
                                className="border rounded p-1 me-2"
                                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                disabled={!table.getCanNextPage()}
                              >
                                {'>>'}
                              </button>
                            </div>
                            <div>
                              <select
                                className="form-select"
                                value={table.getState().pagination.pageSize}
                                onChange={e => {
                                  table.setPageSize(Number(e.target.value))
                                }}
                              >
                                {[10, 20, 30, 40, 50].map(pageSize => (
                                  <option key={pageSize} value={pageSize}>
                                    Show {pageSize}
                                  </option>
                                ))}
                              </select>
                            </div>
                        </div>
                      </>
                    )}
                </Card>
              </div>
            </Col>
          </Row>
        </Page>
      )}


    </React.Fragment>
  );
};

function setColumnFilters(updaterOrValue: Updater<ColumnFiltersState>): void {
  throw new Error('Function not implemented.');
}

interface CustomColumnMeta {
  filterVariant?: 'range' | 'select' | 'text';
}

function Filter({ column }: { column: Column<any, unknown> & { columnDef: { meta?: CustomColumnMeta } } }): JSX.Element {
  const columnFilterValue = column.getFilterValue()
  const { filterVariant } = column.columnDef.meta ?? {}

  return filterVariant === 'range' ? (
    <div>
      <div className="flex space-x-2">
        {/* See faceted column filters example for min max values functionality */}
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[0] ?? ''}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min`}
          className="w-24 border shadow rounded"
        />
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[1] ?? ''}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max`}
          className="w-24 border shadow rounded"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : filterVariant === 'select' ? (
    <select
      onChange={e => column.setFilterValue(e.target.value)}
      value={columnFilterValue?.toString()}
    >
      {/* See faceted column filters example for dynamic select options */}
      <option value="">All</option>
      <option value="complicated">complicated</option>
      <option value="relationship">relationship</option>
      <option value="single">single</option>
    </select>
  ) : (
    <DebouncedInput
      className="form-control"
      onChange={value => column.setFilterValue(value)}
      placeholder={`Search...`}
      type="text"
      value={(columnFilterValue ?? '') as string}
    />
    // See faceted column filters example for datalist search suggestions
  )
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = React.useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  )
}
